
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import StarXpGate from '@/components/star/StarXpGate.vue'
import StarTabBar from '@/components/star/StarTabBar.vue'
import StarNoData from '@/components/star/StarNoData.vue'
import StarCircleMessage from '@/components/star/StarCircleMessage.vue'
import starZoneApi from '@/api/starZoneApi'
import { useStarTheme, useStarRouter } from '@/utils/useStarTheme'
import {
  formatStarTime,
  getCirclePoints,
  getCircleIntro,
} from '@/utils/starZoneHelper'

const defaultImg = require('@/assets/star/logo.png')

export default defineComponent({
  name: 'StarZone',
  components: { StarXpGate, StarTabBar, StarNoData, StarCircleMessage },
  setup() {
    const route = useRoute()
    const store = useStore()
    const { colors, isDark, isLight } = useStarTheme()
    const { starPush } = useStarRouter()
    const activeTab = ref('zone')
    const loading = ref(true)
    const circles = ref<any[]>([])
    const videos = ref<any[]>([])

    const showCircles = computed(() => true)
    const showVideos = computed(() => true)

    const headerStyle = computed(() => ({
      background: isLight.value ? '#f2f5fa' : '#262626',
      borderTop: isLight.value ? '1px solid #dbdfe6' : 'none',
      borderBottom: isLight.value ? '1px solid #dbdfe6' : 'none',
      color: colors.value.textPrimary,
    }))

    const itemStyle = computed(() => ({
      background: isLight.value ? '#fff' : '#262626',
      border: isLight.value ? '1px solid #f0f0f0' : '1px solid #3d3d3d',
    }))

    const liveInfoStyle = computed(() => ({
      background: isLight.value
        ? 'radial-gradient(96.34% 89.63% at 30.98% 0%, #FFF9F4 0%, #F8FAFC 100%)'
        : 'radial-gradient(96.34% 89.63% at 30.98% 0%, #473E36 0%, #201E1F 100%)',
      border: isLight.value ? '1.35px solid #F3EEE9' : '1.35px solid #3D3D3D',
    }))

    const circleIntroStyle = computed(() => ({
      color: isLight.value ? colors.value.textSecondary : 'rgba(255, 255, 255, 0.85)',
    }))

    const circleMsgBubbleStyle = computed(() => ({
      background: isLight.value ? '#f2f5fa' : '#323232',
      color: isLight.value ? '#333' : 'rgba(255, 255, 255, 0.78)',
    }))

    const getVideoCover = (item: any) =>
      item.courseInfo?.coverImage ||
      item.courseInfo?.listImage ||
      item.roomDTO?.image ||
      ''

    const getCircleName = (item: any) =>
      item.roomDTO?.roomName ||
      item.roomDTO?.periodBean?.roomName ||
      item.courseSimpleDTO?.name ||
      item.name ||
      '文字直播'

    const getVideoTitle = (item: any) =>
      item.roomDTO?.roomName ||
      item.roomDTO?.periodBean?.roomName ||
      item.courseInfo?.name ||
      '视频直播'

    const getVideoTime = (item: any) =>
      item.roomDTO?.periodBean?.previewDate ||
      item.roomDTO?.periodBean?.roomVideo?.createTime ||
      item.showTime

    const getVideoTeacherName = (item: any) => {
      const teacherList = item.roomDTO?.simpleTeacherList
      if (Array.isArray(teacherList) && teacherList.length) {
        const names = teacherList
          .map((teacher: any) => teacher.teacherName || teacher.realName || '')
          .filter(Boolean)
        if (names.length) return names.join('、')
      }
      return item.courseInfo?.mainTeacherName || ''
    }

    const getLiveStatusText = (item: any) => {
      const status = item.roomDTO?.roomType
      if (status === 0) return '预告'
      if (status === 1) return '直播中'
      if (status === 2) return '回放'
      if (status === 3) return '直播结束'
      return '回放'
    }

    const openCircle = (item: any) => {
      const roomNo = item.roomDTO?.roomNo || item.courseSimpleDTO?.refId
      if (roomNo) {
        const extraQuery: Record<string, string> = {
          roomNo: String(roomNo),
          roomType: 'text',
        }
        if (item.hasPermission) {
          extraQuery.starInvest = 'true'
        } else {
          extraQuery.freeRoom = '100'
        }
        starPush('/star/quant', extraQuery)
      }
    }

    const scrollingIdx = ref<number | null>(null)
    const scrollPaused = ref(false)

    const onCircleScrollEnter = (idx: number, item: any) => {
      if (getCirclePoints(item).length < 3) return
      scrollingIdx.value = idx
      scrollPaused.value = false
    }

    const onCircleScrollLeave = (idx: number) => {
      if (scrollingIdx.value === idx) {
        scrollPaused.value = true
      }
    }

    const getFeaturedWrapperClass = (idx: number, item: any) => {
      const canScroll = getCirclePoints(item).length >= 3
      return {
        'no-scroll': !canScroll,
        scrolling: canScroll && scrollingIdx.value === idx,
        paused: canScroll && scrollingIdx.value === idx && scrollPaused.value,
      }
    }

    const openVideo = (item: any) => {
      const roomNo = item.roomDTO?.roomNo || item.courseInfo?.refId
      if (roomNo) {
        const extraQuery: Record<string, string> = {
          roomNo: String(roomNo),
          roomType: 'live',
        }
        if (item.hasPermission) {
          extraQuery.starInvest = 'true'
        } else {
          extraQuery.freeRoom = '100'
        }
        starPush('/star/quant', extraQuery)
      }
    }

    const loadData = async () => {
      loading.value = true
      try {
        const token = route.query.token as string
        const username =
          store.state.userInfo?.username ||
          store.state.userInfo?.userName ||
          (route.query.username as string) ||
          (route.query.userName as string) ||
          ''
        const res = await starZoneApi.getInvestTabList({
          headers: { token, username },
        })
        if (res?.code === 1 || res?.code === 0) {
          const data = res.data || {}
          circles.value = data.circles || []
          videos.value = data.videos || []
        }
      } catch (e) {
        console.error('getInvestTabList failed', e)
      } finally {
        loading.value = false
      }
    }

    onMounted(loadData)

    return {
      activeTab,
      loading,
      circles,
      videos,
      colors,
      isDark,
      isLight,
      showCircles,
      showVideos,
      headerStyle,
      itemStyle,
      liveInfoStyle,
      circleIntroStyle,
      circleMsgBubbleStyle,
      defaultImg,
      getCirclePoints,
      getCircleIntro,
      getCircleName,
      getVideoCover,
      getVideoTitle,
      getVideoTime,
      getVideoTeacherName,
      getLiveStatusText,
      formatStarTime,
      openCircle,
      onCircleScrollEnter,
      onCircleScrollLeave,
      getFeaturedWrapperClass,
      openVideo,
    }
  },
})
